import type { PropsWithChildren } from 'react';
import React, { createContext, useEffect, useState, useRef } from 'react';
import i18n, { ParticularConfig } from '@particular.cloud/i18n-js';

export type LangCodeOrLocale = string;
interface I18nContext {
    langCodeOrLocale?: LangCodeOrLocale;
    published?: Date;
    config?: ParticularConfig;
}

export const I18nContext = createContext<I18nContext>({
    langCodeOrLocale: undefined,
    published: undefined,
    config: undefined,
});

export interface I18nProviderProps {
    config?: ParticularConfig;
}

function isServerSide() {
    return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
}

function adaptConfig(
    particularConfig: ParticularConfig | undefined,
    setPublished: (date: Date) => void,
    setLangCodeOrLocale: (langCodeOrLocale: LangCodeOrLocale) => void,
    _setConfig: (config: ParticularConfig) => void,
): ParticularConfig | undefined {
    if (!particularConfig) {
        return undefined;
    }
    const configData = { ...particularConfig };

    // bind to websocket publish event
    if (particularConfig.enableWebsocket) {
        const handlePublish = particularConfig.onPublish;
        configData.onPublish = () => {
            setPublished(new Date());
            if (handlePublish) {
                // if user has defined a custom onPublish, call it
                handlePublish();
            }
        };
    }

    // bind to changes of langCodeOrLocale
    configData.onChangeLanguage = (langCodeOrLocale) => {
        const onChangeLanguage = particularConfig.onChangeLanguage;
        setLangCodeOrLocale(langCodeOrLocale);
        if (onChangeLanguage) {
            onChangeLanguage(langCodeOrLocale);
        }
    };

    configData.onChangeConfig = (config) => {
        const onChangeConfig = particularConfig.onChangeConfig;
        _setConfig(config);
        if (onChangeConfig) {
            onChangeConfig(config);
        }
    };

    return configData;
}

/**
 * I18nProvider: set your Particular.Cloud project token and add options to your ParticularConfig (resets all prior configurations)
 */
export function I18nProvider({ config, children }: PropsWithChildren<I18nProviderProps>) {
    const [langCodeOrLocale, setLangCodeOrLocale] = useState<LangCodeOrLocale | undefined>();
    const [published, setPublished] = useState<Date | undefined>();
    const initiliazedByProviderRef = useRef(false);

    // a pointer to the config object changed by i18n-js
    const [_config, _setConfig] = useState<ParticularConfig | undefined>();

    // our context config
    const [i18nConfig, setI18nConfig] = useState<ParticularConfig | undefined>(
        adaptConfig(config, setPublished, setLangCodeOrLocale, _setConfig),
    );

    // do not wrap in useEffect as this has to run once on the server
    if (isServerSide() && !initiliazedByProviderRef.current) {
        initiliazedByProviderRef.current = true;
        i18n.init(i18nConfig);
    }

    useEffect(() => {
        // changes via React have priority, we do a full init
        i18n.init(adaptConfig(config, setPublished, setLangCodeOrLocale, _setConfig));
        // we don't have to setI18nConfig() here, this will happen through the onChangeConfig callback
    }, [config]);

    useEffect(() => {
        setI18nConfig(_config);
    }, [_config]);

    const context = {
        published,
        config: i18nConfig,
        langCodeOrLocale,
    };

    return <I18nContext.Provider value={context}>{children}</I18nContext.Provider>;
}
